import React from 'react';
import Table, { TableBody } from '@ingka/table';
import Text from '@ingka/text';
import { useTranslation } from 'react-i18next';
import Hyperlink from '@ingka/hyperlink';
import type { FamilyAccount } from '@lotta/shared/types-lottery-schemas';
import styles from '../LotteryConfigOverview.module.scss';
import type { LotteryConfigData } from '../types';

export const TermsTable: React.FC<{
  values: LotteryConfigData;
}> = ({ values: { translation, lottery } }) => {
  const { t } = useTranslation();
  const { familyAccount } = lottery;

  const generateFamilyAccountEmailSubscriptionInfo = (familyAccount: FamilyAccount) => {
    const { emailMarketingEnabled, emailMarketingRequired } =
      familyAccount?.subscriptions?.emailSubscription ?? {};
    return emailMarketingEnabled && emailMarketingRequired
      ? t('general-email-subscription-required')
      : emailMarketingEnabled
      ? t('general-email-subscription-enabled')
      : t('general-email-subscription-disabled');
  };

  return (
    <Table>
      <TableBody className={styles['tbody']}>
        {Object.entries(translation).map(([language, data]) => {
          const capitalizedLanguage = language.toUpperCase();
          const { legal } = data;
          let termsAndConditions: string | undefined = undefined;
          let contestRules: string | undefined = undefined;
          let privacyPolicy: string | undefined = undefined;
          let disclaimerText: string | undefined = undefined;
          if (legal) {
            termsAndConditions = legal.termsAndConditions;
            contestRules = legal.contestRules;
            privacyPolicy = legal.privacyPolicy;
            disclaimerText = legal.disclaimerText;
          }
          return (
            <React.Fragment key={language}>
              {termsAndConditions && (
                <tr key={'termsAndConditions'}>
                  <td className={styles['heading']}>
                    <Text>
                      {t('form-accordion-legal-item-terms-and-conditions-url', {
                        capitalizedLanguage,
                      })}
                    </Text>
                  </td>
                  <td>
                    <Hyperlink url={termsAndConditions} target="_blank">
                      {termsAndConditions}
                    </Hyperlink>
                  </td>
                </tr>
              )}
              {contestRules && (
                <tr key={'contestRules'}>
                  <td className={styles['heading']}>
                    <Text>
                      {t('form-accordion-legal-item-content-rules-url', {
                        capitalizedLanguage,
                      })}
                    </Text>
                  </td>
                  <td>
                    <Hyperlink url={contestRules} target="_blank">
                      {contestRules}
                    </Hyperlink>
                  </td>
                </tr>
              )}
              {privacyPolicy && (
                <tr key={'privacyPolicy'}>
                  <td className={styles['heading']}>
                    <Text>
                      {t('form-accordion-legal-item-privacy-policy-url', {
                        capitalizedLanguage,
                      })}
                    </Text>
                  </td>
                  <td>
                    <Hyperlink url={privacyPolicy} target="_blank">
                      {privacyPolicy}
                    </Hyperlink>
                  </td>
                </tr>
              )}
              {familyAccount && (
                <tr key="familyAccountEmailSubscription">
                  <td className={styles['heading']}>
                    <Text>{t('general-email-subscription')}</Text>
                  </td>
                  <td>
                    <Text>{generateFamilyAccountEmailSubscriptionInfo(familyAccount)}</Text>
                  </td>
                </tr>
              )}
              {disclaimerText && (
                <tr key={'disclaimerText'}>
                  <td className={styles['heading']}>
                    <Text>
                      {t('form-accordion-legal-item-disclaimer-text', {
                        capitalizedLanguage,
                      })}
                    </Text>
                  </td>
                  <td>
                    <Text>{disclaimerText}</Text>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
};
